import { useState } from "react";
import { basisLabel, fromNet, toCents, toNet, toNumber } from "../../shared/money";
import { ChoiceField, SelectField, TextField } from "../../web/components/fields";
import { Box, ErrorNotice, Notice, SectionCard } from "../../web/components/ui";
import { useMoney } from "../../web/lib/money";
import { can, itemName, itemTax, usePriceListSaver } from "./data";
import { duration } from "./describe";
import { inUse, isHourlyLabour, isProduct, LABOUR_MINUTES_RANGE, MAX_LABOUR_MINUTES, MIN_LABOUR_MINUTES, type MaterialRow, type PriceListData } from "./meta";
import { MarkupHint } from "./PackageCards";
import { labourShare, needsCheck, shownPercent } from "./pricing";
import { MODES, type PriceChoice } from "./PricingEditor";
import { QuotedAmountField } from "./QuotedAmount";
import { usePriceListWindows } from "./windows";

/*
 * Labour time on a product's Price tab (labourTime.ts): a set time on an hourly labour product, its price (and cost) that share of
 * the rate's, e.g. "Install standard lockset": 30 min of Labour Per Hour. Changed here as it's typed (no draft: there's only the
 * rate and the time). On the rate's own Price tab, the products that are a share of it, to update together when it changes.
 */

/** An item's price or cost before tax. */
const netOf = (data: PriceListData, m: MaterialRow, field: "price" | "cost") => toNet(toNumber(m[field]), m.price_includes_taxes, itemTax(data, m).tax);

/** What a product can be a labour time on: hourly labour products in use (isHourlyLabour; labourTime.ts: not items sold in ServiceM8), not labour times themselves, nor it. */
export const labourRateChoices = (data: PriceListData, item: MaterialRow) =>
    data.materials.filter((m) => m.uuid !== item.uuid && isProduct(m) && inUse(m) && isHourlyLabour(m.name) && !data.itemPricing[m.uuid]?.labour);

/** The products that are a labour time on this rate. */
export const labourSharesOf = (data: PriceListData, rateUuid: string) => data.materials.filter((m) => data.itemPricing[m.uuid]?.labour?.rateUuid === rateUuid);

/** Why a product can't be made a labour time, if it can't: no rate to be one on, or others are one on it (one level only). */
export function labourUnavailable(data: PriceListData, item: MaterialRow) {
    if (labourSharesOf(data, item.uuid).length) return "Other products are labour time on this one.";
    return labourRateChoices(data, item).length ? undefined : "Needs an hourly labour product in the Catalogue: one with Labour in its name.";
}

/** A labour time to start from: the first rate, and the time its price comes to on it (to the minute), so its price barely moves. */
export function startingLabour(data: PriceListData, item: MaterialRow) {
    const choices = labourRateChoices(data, item);
    const rate = choices.find((m) => /\b(hour|hourly|hr)\b/i.test(m.name)) ?? choices[0];
    if (!rate) return null;
    const hourly = netOf(data, rate, "price");
    const minutes = hourly > 0 ? Math.round((netOf(data, item, "price") / hourly) * 60) : 60;
    return { rateUuid: rate.uuid, minutes: Math.min(MAX_LABOUR_MINUTES, Math.max(MIN_LABOUR_MINUTES, minutes)) };
}

/**
 * A product that's a labour time, in its Price card: how it's priced (leaving: Manual keeps its price, Markup works one out), its rate
 * and time, then what they give laid out as Manual's and Markup's are: its markup (on the cost the rate gives it) and its price.
 */
export function LabourTimeCard(
    props: Readonly<{ data: PriceListData; item: MaterialRow; onLeave: (to: "manual" | "percent") => Promise<unknown>; /** Open its Costing tab (its cost, in the markup's hint). */ onCostTab: () => void }>,
) {
    const { data, item } = props;
    const fmt = useMoney();
    const saver = usePriceListSaver();
    const [error, setError] = useState<Error | null>(null);
    const link = data.itemPricing[item.uuid]?.labour;
    if (!link) return null;
    const canEdit = can(data, "items.pricing");
    const rate = data.materials.find((m) => m.uuid === link.rateUuid);
    const { tax } = itemTax(data, item);
    // In the item's basis: the rate's hour, and its share.
    const shown = (net: number) => fromNet(net, item.price_includes_taxes, tax);
    const hourlyPrice = rate ? netOf(data, rate, "price") : 0;
    // Before tax: its price and cost as the rate gives them (the rate gone: as they are), and the markup that makes.
    const priceNet = rate ? labourShare(hourlyPrice, link.minutes) : netOf(data, item, "price");
    const costNet = rate ? labourShare(netOf(data, rate, "cost"), link.minutes) : netOf(data, item, "cost");
    const markupPct = costNet > 0 ? ((priceNet - costNet) / costNet) * 100 : null;
    const label = basisLabel(item.price_includes_taxes, itemTax(data, item).rate);
    const quote = { rateUuid: itemTax(data, item).uuid, includesTax: item.price_includes_taxes };
    const save = (next: { rateUuid: string; minutes: number }) => {
        setError(null);
        return saver.setLabourTime(item.uuid, next).catch((err: Error) => {
            setError(err);
            throw err;
        });
    };
    const choices = labourRateChoices(data, item).map((m) => ({ value: m.uuid, label: `${itemName(data, m)} · ${fmt.money(shown(netOf(data, m, "price")))}/hr` }));
    if (!rate) choices.unshift({ value: link.rateUuid, label: "A rate that's gone" });
    return (
        <div className="space-y-4">
            <ChoiceField<PriceChoice> label="Price is" hideLabel value="labour" disabled={!canEdit} options={MODES} onSave={(to) => (to === "labour" ? Promise.resolve() : props.onLeave(to))} />
            {link.rateMissing ? <Notice tone="warning">Its labour rate is gone, so its price stays as it was. Choose another rate.</Notice> : null}
            <div className="grid gap-4 sm:grid-cols-2">
                <SelectField label="Labour rate" value={link.rateUuid} disabled={!canEdit} options={choices} onSave={(rateUuid) => save({ rateUuid, minutes: link.minutes })} />
                <TextField
                    label="Time"
                    value={String(link.minutes)}
                    suffix="min"
                    inputMode="decimal"
                    disabled={!canEdit}
                    validate={(v) => (Number(v) >= MIN_LABOUR_MINUTES && Number(v) <= MAX_LABOUR_MINUTES ? null : LABOUR_MINUTES_RANGE)}
                    hint={link.minutes >= 60 ? duration(link.minutes) : undefined}
                    onSave={(v) => save({ rateUuid: link.rateUuid, minutes: Number(v) })}
                />
            </div>
            {/* What they give, as Manual's and Markup's fields are laid out: its markup on the cost the rate gives it (the cost under it,
                to its Costing tab), and its price (how it's taxed picked inside it), both worked out, not typed. */}
            <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                    label="Markup"
                    value={markupPct === null ? "" : shownPercent(String(markupPct))}
                    placeholder="-"
                    suffix="%"
                    disabled
                    onSave={() => Promise.resolve()}
                    hint={
                        <MarkupHint
                            priceNet={priceNet}
                            costNet={costNet}
                            shown={shown}
                            label={label}
                            onCostTab={props.onCostTab}
                            noCost={rate ? <>Needs a cost: {itemName(data, rate)} has none</> : undefined}
                        />
                    }
                />
                <QuotedAmountField
                    label="Total price"
                    amount={toCents(shown(priceNet))}
                    quote={quote}
                    rates={data.taxRates}
                    disabled={!canEdit}
                    amountLocked
                    hint={rate ? `${duration(link.minutes)} × ${fmt.money(shown(hourlyPrice))}/hr` : undefined}
                    hintFirst
                    onSave={() => Promise.resolve()}
                    onQuote={(t) => saver.updateMaterial(item.uuid, { tax_rate_uuid: t.rateUuid, price_includes_taxes: t.includesTax })}
                />
            </div>
            <ErrorNotice error={error} />
        </div>
    );
}

/**
 * On an hourly labour rate's Price tab: the products that are labour time on it, their time and price (they follow it at once:
 * productsFollow.ts; their items then show theirs to update).
 */
export function LabourSharesCard({ data, rate }: Readonly<{ data: PriceListData; rate: MaterialRow }>) {
    const fmt = useMoney();
    const windows = usePriceListWindows();
    const shares = labourSharesOf(data, rate.uuid);
    if (!shares.length) return null;
    const checkOf = (m: MaterialRow) => needsCheck(m, itemTax(data, m), data.itemPricing[m.uuid]);
    return (
        <Box>
            <SectionCard
                label="Labour times"
                aside="Priced from this rate"
                bodyClassName=""
            >
                <ul className="m-0 divide-y divide-line p-0">
                    {shares.map((m) => {
                        const check = checkOf(m);
                        const link = data.itemPricing[m.uuid]?.labour;
                        return (
                            <li key={m.uuid} className="flex items-center gap-3 px-3 py-2">
                                <button type="button" className="link-button min-w-0 flex-1 truncate text-left" title={itemName(data, m)} onClick={() => windows.openItem(m.uuid)}>
                                    {itemName(data, m)}
                                </button>
                                <span className="shrink-0 text-xs text-muted">{link ? duration(link.minutes) : ""}</span>
                                <span className="shrink-0 text-right tabular-nums">
                                    {check ? (
                                        <>
                                            <span className="text-muted">{fmt.money(m.price)}</span> → <strong className="text-warning-ink">{fmt.money(check.target)}</strong>
                                        </>
                                    ) : (
                                        fmt.money(m.price)
                                    )}
                                </span>
                            </li>
                        );
                    })}
                </ul>
            </SectionCard>
        </Box>
    );
}
